if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (this.dataset.inline === 'true') {
          if (!this.anchor) {
            this.anchor = document.createComment('quick-buy-home');
            this.before(this.anchor);
          }
        } else super.connectedCallback();
      }

      showInline(opener) {
        document.querySelectorAll('quick-add-modal[data-inline="true"][open]').forEach((panel) => {
          if (panel !== this) panel.hide(true);
        });
        this.openedBy = opener;
        const item = opener.closest('.grid__item');
        const grid = item?.parentElement;
        if (item && grid && !grid.classList.contains('slider')) {
          const rowTop = item.getBoundingClientRect().top;
          const row = [...grid.children].filter((node) => !node.classList.contains('rt-quick-panel') && Math.abs(node.getBoundingClientRect().top - rowTop) < 3);
          this.panelSlot = document.createElement(grid.tagName === 'UL' ? 'li' : 'div');
          this.panelSlot.className = 'grid__item rt-quick-panel';
          (row.at(-1) || item).after(this.panelSlot);
        } else {
          this.panelSlot = document.createElement('div');
          this.panelSlot.className = 'rt-quick-panel';
          (opener.closest('slider-component') || item || opener.parentElement).after(this.panelSlot);
        }
        this.panelSlot.appendChild(this);
        const content = this.querySelector('[role="dialog"]');
        content.setAttribute('aria-modal', 'false');
        this.setAttribute('open', '');
        opener.setAttribute('aria-expanded', 'true');
        content.focus({ preventScroll: true });
        this.panelSlot.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (this.dataset.inline === 'true') {
          this.removeAttribute('open');
          this.openedBy?.setAttribute('aria-expanded', 'false');
          if (this.anchor?.isConnected) this.anchor.after(this);
          this.panelSlot?.remove();
          this.panelSlot = null;
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          if (!preventFocus) this.openedBy?.focus();
          window.pauseAllMedia();
        } else {
          if (preventFocus) this.openedBy = null;
          super.hide();
        }
      }

      show(opener) {
        if (opener.getAttribute('aria-disabled') === 'true') return;
        if (this.hasAttribute('open')) { this.querySelector('[role="dialog"]')?.focus(); return; }
        const requestToken = {};
        window.rtQuickBuyRequest = requestToken;
        if (this.dataset.inline === 'true' && matchMedia('(max-width: 749px)').matches) {
          location.assign(opener.getAttribute('data-product-url'));
          return;
        }
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => { if (!response.ok) throw new Error('Product unavailable'); return response.text(); })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (window.rtQuickBuyRequest !== requestToken) return;
            if (!productElement || !opener.isConnected) throw new Error('Product unavailable');
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            if (this.dataset.inline === 'true') this.showInline(opener);
            else super.show(opener);
          })
          .catch(() => {
            if (window.rtQuickBuyRequest !== requestToken || !opener.isConnected) return;
            const fallback = document.createElement('a');
            fallback.href = opener.getAttribute('data-product-url');
            fallback.className = 'link';
            fallback.textContent = this.dataset.error || opener.textContent.trim();
            const previous = opener.parentElement.querySelector('[data-quick-buy-error]');
            previous?.remove();
            fallback.setAttribute('data-quick-buy-error', '');
            opener.after(fallback);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
